import React, { type SyntheticEvent, useState } from 'react'
import {
  Box,
  Button,
  Card,
  Flex,
  getPaletteColor,
  Hide,
  Text,
  theme
} from 'pcln-design-system'
import styled from 'styled-components'
import { PriceTag } from '@pcln/vip'
import { Check, ChevronDown } from 'pcln-icons'
import ClickOutsideWrapper from '@/components/ClickOutsideWrapper'

const Container = styled(Box)`
  z-index: 11;
  position: absolute;
  top: 100px;
  left: 0;
  right: 0;
  ${theme.mediaQueries.md} {
    top: -35px;
  }
`
const Wrapper = styled(Card)<{ toggle: boolean }>`
  display: ${props => (props.toggle ? 'block' : 'none')};
  ${theme.mediaQueries.lg} {
    display: block;
  }
  margin: auto;
  max-width: 1280px;
`
const Bar = styled(Flex)`
  background: ${getPaletteColor('background.lightest')};
  border-radius: 6px;
`
const Item = styled(Button)`
  -webkit-font-smoothing: auto;
  border-radius: 6px;
  color: ${getPaletteColor('primary.base')};
  background: ${getPaletteColor('background.lightest')};
  font-family: Montserrat;
  font-weight: 500;
  cursor: pointer;
  padding: 7px 0;
  &:hover {
    color: ${getPaletteColor('primary.base')};
    text-decoration: none;
    background: ${getPaletteColor('background.base')};
  }
  &:active {
    color: ${getPaletteColor('text.lightest')};
    background: ${getPaletteColor('primary.base')};
  }
  ${theme.mediaQueries.lg} {
    color: ${getPaletteColor('text.light')};
    background: ${getPaletteColor('background.light')};
    border-radius: 0px;
    &:hover {
      color: ${getPaletteColor('primary.base')};
      background: ${getPaletteColor('background.base')};
    }
    &:active {
      color: ${getPaletteColor('primary.base')};
      background: ${getPaletteColor('background.base')};
    }
    &:first-of-type {
      border-radius: 6px 0 0 6px;
      border-right: 1px solid ${getPaletteColor('border.base')};
    }
    &:last-of-type {
      border-radius: 0 6px 6px 0;
      border-left: 1px solid ${getPaletteColor('border.base')};
    }
    &:nth-of-type(2) {
      border-right: 1px solid ${getPaletteColor('border.base')};
    }
  }
`
const CheckIcon = styled(Check)`
  visibility: hidden;
  ${Item}:active & {
    visibility: visible;
  }
`

type NavBarCategory = {
  id: number
  label: string
  anchor: string
  icon?: JSX.Element
}

export default function NavBar({
  categories
}: {
  categories: NavBarCategory[]
}) {
  const [isToggleVisible, setIsToggleVisible] = useState(false)
  const itemWidth = `${100 / categories.length}%`
  return (
    <Container width={1} px={[3, 5, 5, 3, 4, 5]}>
      <ClickOutsideWrapper setIsPanelOpen={() => setIsToggleVisible(false)}>
        <Box>
          <Hide
            lg
            xl
            xxl
            bg="background.light"
            mb={2}
            py={1}
            color="primary.base"
            style={{
              borderRadius: '6px'
            }}
            onClick={() => {
              setIsToggleVisible(!isToggleVisible)
            }}
          >
            <Flex p={1}>
              <PriceTag pl={1} fontSize="40px" variation="vip" />
              <Text m="auto" pr={2}>
                VIP Benefits
              </Text>
              <ChevronDown my="auto" mr={2} />
            </Flex>
          </Hide>
          <Wrapper
            toggle={isToggleVisible}
            id="toggle"
            boxShadowSize="xl"
            borderWidth={0}
            borderRadius={6}
          >
            <Bar
              flexDirection={['column', null, null, 'row']}
              p={[2, null, null, 0]}
              mx="auto"
            >
              {categories.map(item => (
                <Item
                  key={item.id}
                  width={[1, null, null, itemWidth]}
                  mb={[1, null, null, 0]}
                  onClick={(e: SyntheticEvent) => {
                    e.preventDefault()
                    const target = document.getElementById(item.anchor)
                    if (target) {
                      target.scrollIntoView({ behavior: 'smooth' })
                    }
                    setIsToggleVisible(false)
                  }}
                >
                  <Box
                    m="auto"
                    width={['100%', null, null, 'fit-content']}
                    style={{
                      gridTemplateColumns: '40px auto 40px'
                    }}
                    display={['grid', null, null, 'flex']}
                  >
                    <Box>{item.icon}</Box>
                    <Text
                      py={[2, null, null, 3]}
                      fontSize={[2, null, null, 1, 2, 2]}
                      m="auto"
                      textAlign="center"
                    >
                      {item.label}
                    </Text>
                    <Hide lg xl xxl>
                      <CheckIcon mr={2} my={2} />
                    </Hide>
                  </Box>
                </Item>
              ))}
            </Bar>
          </Wrapper>
        </Box>
      </ClickOutsideWrapper>
    </Container>
  )
}
